import type { AlertReason } from './alerts';
import { renderEmailHtml } from './email-layout';
import { SERVICE_TYPE_LABELS, type ServiceType } from './service-types';

/**
 * The text of alert emails (Phase 07 Stage 05; DECISIONS.md A-AL5, A-NT1). Only the approved
 * fields: the service's portal name, its state, the reason in plain words, the opened or recovery
 * time (Africa/Lagos) and the link to the service page. Never a check target, host name,
 * credential, token, configuration or other technical detail, and never a claim that the email was
 * delivered. The same email goes to Admin and ICT recipients. Sent as branded HTML with this plain
 * text alongside (A-EM1); both carry the same content.
 */
export interface AlertEmail {
  subject: string;
  text: string;
  html: string;
}

const REASON_TEXT: Record<AlertReason, string> = {
  check_failing: 'A check is failing.',
  check_warning: 'A check reported a warning.',
};

const LAGOS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Africa/Lagos',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** "5 Oct 2026, 14:02 WAT" — Africa/Lagos is always UTC+1 (WAT). */
export function formatLagosTime(at: Date): string {
  return `${LAGOS.format(at)} WAT`;
}

function serviceLink(portalOrigin: string, serviceId: string): string {
  return `${portalOrigin.replace(/\/+$/, '')}/app/services/${encodeURIComponent(serviceId)}`;
}

const FOOTER = 'This is an automatic message from the Bekebi Portal.';

export function composeAlertOpenedEmail(input: {
  serviceType: ServiceType;
  serviceId: string;
  reason: AlertReason;
  openedAt: Date;
  portalOrigin: string;
}): AlertEmail {
  const name = SERVICE_TYPE_LABELS[input.serviceType];
  const opened = formatLagosTime(input.openedAt);
  const link = serviceLink(input.portalOrigin, input.serviceId);
  return {
    subject: `[Bekebi Portal] ${name} needs attention`,
    text: [
      `${name} needs attention.`,
      '',
      `Service: ${name}`,
      'State: Needs attention',
      `Reason: ${REASON_TEXT[input.reason]}`,
      `Opened: ${opened}`,
      '',
      'View the service in the Bekebi Portal:',
      link,
      '',
      FOOTER,
      '',
    ].join('\n'),
    html: renderEmailHtml({
      portalOrigin: input.portalOrigin,
      preheader: `${REASON_TEXT[input.reason]} Opened ${opened}.`,
      kind: 'Service alert',
      tone: 'attention',
      status: 'Needs attention',
      title: `${name} needs attention`,
      paragraphs: [
        `${REASON_TEXT[input.reason]} The portal will email you again when ${name} has recovered.`,
      ],
      details: [
        ['Service', name],
        ['State', 'Needs attention'],
        ['Reason', REASON_TEXT[input.reason]],
        ['Opened', opened],
      ],
      action: { label: 'View the service', url: link },
    }),
  };
}

export function composeAlertRecoveredEmail(input: {
  serviceType: ServiceType;
  serviceId: string;
  resolvedAt: Date;
  portalOrigin: string;
}): AlertEmail {
  const name = SERVICE_TYPE_LABELS[input.serviceType];
  const recovered = formatLagosTime(input.resolvedAt);
  const link = serviceLink(input.portalOrigin, input.serviceId);
  return {
    subject: `[Bekebi Portal] ${name} has recovered`,
    text: [
      `${name} has recovered.`,
      '',
      `Service: ${name}`,
      'State: Healthy',
      'Reason: Its checks are passing again.',
      `Recovered: ${recovered}`,
      '',
      'View the service in the Bekebi Portal:',
      link,
      '',
      FOOTER,
      '',
    ].join('\n'),
    html: renderEmailHtml({
      portalOrigin: input.portalOrigin,
      preheader: `Its checks are passing again. Recovered ${recovered}.`,
      kind: 'Service alert',
      tone: 'recovered',
      status: 'Healthy',
      title: `${name} has recovered`,
      paragraphs: ['Its checks are passing again. No action is needed.'],
      details: [
        ['Service', name],
        ['State', 'Healthy'],
        ['Recovered', recovered],
      ],
      action: { label: 'View the service', url: link },
    }),
  };
}

/**
 * The Admin's test email (Phase 07 Stage 05-C): proves the portal can hand a message to the mail
 * server. Sent only to the Admin who asked for it; it is not an alert and names no service.
 */
export function composeTestEmail(input: { requestedAt: Date; portalOrigin: string }): AlertEmail {
  const origin = input.portalOrigin.replace(/\/+$/, '');
  const requested = formatLagosTime(input.requestedAt);
  return {
    subject: '[Bekebi Portal] Test email',
    text: [
      'This is a test email from the Bekebi Portal.',
      '',
      'You asked for it from Monitoring → Alerts to check that the portal can send alert emails.',
      'No service needs attention because of this message, and no action is needed.',
      '',
      `Requested: ${requested}`,
      '',
      'Bekebi Portal:',
      origin,
      '',
      FOOTER,
      '',
    ].join('\n'),
    html: renderEmailHtml({
      portalOrigin: input.portalOrigin,
      preheader: 'The portal can send alert emails. No action is needed.',
      kind: 'Test email',
      tone: 'neutral',
      status: 'Email is working',
      title: 'Your portal email works',
      paragraphs: [
        'You asked for this from Monitoring → Alerts to check that the portal can send alert emails.',
        'No service needs attention because of this message, and no action is needed.',
      ],
      details: [['Requested', requested]],
      action: { label: 'Open the Bekebi Portal', url: origin },
    }),
  };
}
